import type { AxiosRequestConfig, AxiosResponse, Method } from 'axios';

export type WorkerHttpResult = {
  ok: boolean;
  status: number;
  statusText: string;
  url: string;
  headers: Record<string, string>;
  data: unknown;
  message?: string;
};

type WorkerHttpResponseMessage = {
  id: number;
  ok: boolean;
  status: number;
  statusText: string;
  url: string;
  headers: Record<string, string>;
  data: unknown;
  message?: string;
};

class HttpWorkerClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: WorkerHttpResult) => void; reject: (e: unknown) => void }>();

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;

    //worker import.
    this.worker = new Worker(new URL('../workers/httpWorker.ts', import.meta.url), { type: 'module' });

    this.worker.onmessage = (event: MessageEvent<WorkerHttpResponseMessage>) => {
      const msg = event.data;
      const entry = this.pending.get(msg.id);
      if (!entry) return;
      this.pending.delete(msg.id);

      entry.resolve({
        ok: msg.ok,
        status: msg.status,
        statusText: msg.statusText,
        url: msg.url,
        headers: msg.headers,
        data: msg.data,
        message: msg.message,
      });
    };

    this.worker.onerror = (event) => {
      // Fail all in-flight requests if the worker dies.
      for (const { reject } of this.pending.values()) {
        reject(event);
      }
      this.pending.clear();
      this.worker = null;
    };

    return this.worker;
  }

  async request(url: string, method: Method, headers?: Record<string, string>, body?: unknown): Promise<WorkerHttpResult> {
    const worker = this.ensureWorker();
    const id = this.nextId++;

    return new Promise<WorkerHttpResult>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      worker.postMessage({ id, url, method, headers, body });
    });
  }
}

const singleton = new HttpWorkerClient();

function appendParams(url: string, params: unknown): string {
  if (!params || typeof params !== 'object') return url;

  const u = new URL(url, window.location.origin);

  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        u.searchParams.append(key, String(item));
      }
      continue;
    }

    u.searchParams.set(key, String(value));
  }

  return u.toString();
}

export async function requestViaWorker(axiosRequestConfig: AxiosRequestConfig): Promise<AxiosResponse> {
  const method = (axiosRequestConfig.method || 'GET') as Method;
  const fullUrl = appendParams(String(axiosRequestConfig.url), axiosRequestConfig.params);

  const headers = (axiosRequestConfig.headers || {}) as Record<string, string>;

  const result = await singleton.request(fullUrl, method, headers, axiosRequestConfig.data);

  // Return a minimal AxiosResponse-shaped object.
  return {
    data: result.data,
    status: result.status,
    statusText: result.statusText,
    headers: result.headers,
    config: axiosRequestConfig as any,
    request: { responseURL: result.url } as any,
  } as AxiosResponse;
}
